import React, { useMemo, useState } from 'react';
import { Folder, Tag } from 'lucide-react';

import { buildCategoryIndex, descendantIds } from '../../utils/categoryTree';

const MAX_CATEGORY_DEPTH = 10; // mirrors the server constant (Category.MAX_CATEGORY_DEPTH)

// Breadcrumb-style category picker: drill down through the tree one level at
// a time (same interaction as the storefront's category browse), rather than
// one giant flat <select> — depth can go to 10 with an unbounded sibling
// count per level. `categories` is the full flat admin list (already loaded
// by useAdminCategories).
//
// Two modes:
//   - default (CategoryForm's parent picker): any category is a valid choice
//     (a subcategory's parent can itself be a leaf or a non-leaf), so every
//     click both selects that category and drills into it. `excludeId` omits
//     the category being edited and its own descendants, since either would
//     create a cycle.
//   - `leafOnly` (ProductEditor's category picker): only a leaf is a valid
//     final choice (products can't live on a category that has children), so
//     clicking a non-leaf only drills in without selecting it, while clicking
//     a leaf selects it (it has no children to drill into anyway).
export default function CategoryParentPicker({ categories, value, onChange, excludeId, leafOnly = false }) {
  const { byId, depthOf, childrenOf } = useMemo(() => buildCategoryIndex(categories), [categories]);

  const excluded = useMemo(() => {
    if (!excludeId) return new Set();
    return new Set([excludeId, ...descendantIds(childrenOf, excludeId)]);
  }, [excludeId, childrenOf]);

  // Breadcrumb trail of selected ids, root-first — seeded from `value` once,
  // then tracked locally as the admin drills further or jumps back up.
  const [trail, setTrail] = useState(() => {
    if (!value || !byId.has(value)) return [];
    const path = [];
    let cur = byId.get(value);
    while (cur) {
      path.unshift(cur._id);
      cur = cur.parentId ? byId.get(cur.parentId) : null;
    }
    return path;
  });

  const levelKey = trail[trail.length - 1] || 'root';
  const options = (childrenOf.get(levelKey) || []).filter((c) => !excluded.has(c._id));

  const choose = (cat) => {
    setTrail((t) => [...t, cat._id]);
    if (!leafOnly || cat.isLeaf) onChange(cat._id);
  };

  // In leafOnly mode the trail is browse-only navigation — jumping to an
  // ancestor doesn't re-select it, since a non-leaf is never a valid final
  // choice; the selection only changes when a leaf is actually clicked.
  const goToRoot = () => {
    setTrail([]);
    if (!leafOnly) onChange(null);
  };

  const goTo = (index) => {
    const next = trail.slice(0, index + 1);
    setTrail(next);
    if (!leafOnly) onChange(next[next.length - 1] || null);
  };

  return (
    <div className="ss-parent-picker">
      <nav className="ss-parent-picker__trail" aria-label="Selected parent category">
        <button type="button" onClick={goToRoot} className={trail.length === 0 ? 'is-active' : ''}>
          Top level
        </button>
        {trail.map((id, i) => (
          <React.Fragment key={id}>
            <span aria-hidden="true">/</span>
            <button
              type="button"
              onClick={() => goTo(i)}
              className={i === trail.length - 1 ? 'is-active' : ''}
            >
              {byId.get(id)?.name}
            </button>
          </React.Fragment>
        ))}
      </nav>

      {options.length > 0 ? (
        <ul className="ss-parent-picker__list">
          {options.map((c) => {
            // a category at the max depth can't itself take a child without
            // exceeding the 10-level limit (mirrors validateParent server-side)
            // — irrelevant in leafOnly mode, since picking an existing leaf
            // for a product never adds new nesting.
            const disabled = !leafOnly && depthOf.get(c._id) >= MAX_CATEGORY_DEPTH - 1;
            const title = disabled
              ? `Max depth (${MAX_CATEGORY_DEPTH} levels) reached`
              : leafOnly
                ? (c.isLeaf ? 'Select this category' : 'Browse its subcategories')
                : 'Choose and view its subcategories';
            return (
              <li key={c._id}>
                <button
                  type="button"
                  className="ss-parent-picker__option"
                  onClick={() => choose(c)}
                  disabled={disabled}
                  title={title}
                >
                  {leafOnly && (c.isLeaf ? <Tag size={14} aria-hidden="true" /> : <Folder size={14} aria-hidden="true" />)}
                  {c.name}
                  {!leafOnly && !c.isLeaf && <span className="ss-parent-picker__badge">has subcategories</span>}
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="ss-parent-picker__empty">No subcategories here.</p>
      )}
    </div>
  );
}
